import { useEffect, useRef, useState } from "react";

import { loadSandbox } from "../sim/loadSandbox";
import { shapeBounds, stampShape, type ShapeDef } from "../sim/shapes";
import type { MaterialDef, SandboxBundle } from "../sim/types";
import { createRenderer, type CellRenderer } from "../three/renderer";

export interface SimCanvasProps {
  width: number;
  height: number;
  pixelScale: number;
  brushRadius: number;
  brushKelvin: number;
  selectedMaterialId: number;
  /** When non-null, clicks stamp this shape instead of painting. */
  selectedShape: ShapeDef | null;
  heatMode: boolean;
  /** When true, single-click toggles a `tags: [trigger]` cell at
   *  the click location (e.g. on/off switches) instead of
   *  painting. The double-click handler keeps working in every
   *  mode as a power-user shortcut. */
  actionMode: boolean;
  /** When true, click-and-drag pans the canvas-host scroll position
   *  (no painting, no toggling); double-click lifts the 4-connected
   *  same-material region under the cursor into a held buffer, and
   *  the next single click drops the held cells at the new location.
   *  Mutually exclusive with the other tool modes. */
  moveMode: boolean;
  /** When true, single-click on a `cpu` machine (chip or its RAM /
   *  screen) opens the Code Tool panel for that CPU via
   *  `onCodeClick`. Mutually exclusive with the other tool modes. */
  codeMode: boolean;
  /** Called with the clicked grid cell when the Code tool is active
   *  and the cell belongs to a CPU. App opens the Code panel for it. */
  onCodeClick: (x: number, y: number) => void;
  /** When true, single-click on a `dimension` portal cell enters that
   *  dimension (switching the viewed layer to its child world). A
   *  navigation tool: it never paints. Mutually exclusive with the
   *  other tool modes. */
  dimensionMode: boolean;
  /** Reports the current view depth (0 at the main world, 1+ inside a
   *  dimension) whenever it changes, so App can render a breadcrumb /
   *  back button. */
  onLayerChange: (depth: number) => void;
  paused: boolean;
  /** Canvas-host background class name (set by the toolbar). */
  backgroundClass?: string;
  /** When true, an inline magnifying-glass overlay follows the cursor. */
  magnifierActive: boolean;
  /** Linear zoom factor for the magnifier (1.5..16 in the UI). */
  magnifierZoom: number;
  /** When true, a single click on the canvas zooms the canvas one
   * rung in centred on the clicked cell (shift-click zooms one rung
   * out). Mutually exclusive with the other click-driven tools. */
  zoomToolActive: boolean;
  /** Walk one rung up or down the parent's shared zoom ladder.
   * `direction = +1` zooms in, `-1` zooms out. */
  computeNextZoom: (current: number, direction: 1 | -1) => number;
  /** Apply a new canvas pixel scale. SimCanvas stashes the click
   * anchor and re-scrolls the canvas-host once the new scale lands
   * so the clicked cell stays under the cursor. */
  onPixelScaleChange: (next: number) => void;
  onReady: (bundle: SandboxBundle) => void;
  onTickInfo: (info: { tick: number; backend: string }) => void;
}

interface ZoomAnchor {
  gridX: number;
  gridY: number;
  hostX: number;
  hostY: number;
  targetScale: number;
}

// Diameter of the magnifier overlay in CSS pixels. Picked by feel:
// large enough to read pixel-level detail at 4x without dominating
// the canvas, small enough to keep the cursor area visible around it.
const MAGNIFIER_DIAMETER = 200;

export function SimCanvas(props: SimCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  // The scrollable `.canvas-host` div — the zoom-tool re-anchors its
  // scrollLeft / scrollTop after a click so the clicked cell stays
  // under the cursor across the zoom step.
  const hostRef = useRef<HTMLDivElement | null>(null);
  // Click anchor stashed by the zoom-tool at pointerdown; consumed
  // by the resize effect once `props.pixelScale` reflects the new
  // rung the click requested.
  const pendingZoomAnchorRef = useRef<ZoomAnchor | null>(null);
  const bundleRef = useRef<SandboxBundle | null>(null);
  const rendererRef = useRef<CellRenderer | null>(null);
  // Overlay div for the live shape-resize preview. Updated
  // imperatively from the rAF loop.
  const previewRef = useRef<HTMLDivElement | null>(null);
  const previewLabelRef = useRef<HTMLSpanElement | null>(null);
  // Magnifier overlay: a circular wrapper containing a 2D canvas
  // that drawImages the relevant slice of the main WebGL canvas
  // each frame. Position + visibility updated in the rAF loop too.
  const magnifierRef = useRef<HTMLDivElement | null>(null);
  const magnifierCanvasRef = useRef<HTMLCanvasElement | null>(null);
  // Action-mode hover ring: yellow outline that follows the
  // cursor when the user is over a `tags: [trigger]` cell. Only
  // visible while action mode is on. The polling lives in the
  // rAF loop so the overlay tracks the cursor smoothly even
  // while the simulation is paused.
  const actionHoverRef = useRef<HTMLDivElement | null>(null);
  const [error, setError] = useState<string | null>(null);

  const propsRef = useRef(props);
  propsRef.current = props;

  // Init effect — runs once per mount (only re-runs if width/height
  // change, which would mean a different sandbox shape entirely).
  // pixelScale changes are handled by a separate resize effect below
  // so zooming doesn't blow away the wasm sandbox state.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    let cancelled = false;
    let rafId = 0;
    let pointerDown = false;
    let lastX = 0;
    let lastY = 0;
    // Latest cursor position in CSS pixels, anchored to the
    // canvas-host (the magnifier's positioning parent).
    let cursorHostX = 0;
    let cursorHostY = 0;
    let cursorOnCanvas = false;
    // Anchor + scale for the in-progress shape drag.
    let shapeAnchorX = 0;
    let shapeAnchorY = 0;
    let shapeScale = 1;
    let dragHasMoved = false;
    // Move-tool state: panning the canvas-host scroll while the
    // pointer is held down, plus a "held" buffer of lifted cells
    // waiting for a click to drop them.
    let movePanning = false;
    let panStartClientX = 0;
    let panStartClientY = 0;
    let panStartScrollLeft = 0;
    let panStartScrollTop = 0;
    let liftedCells: { dx: number; dy: number; material: number }[] | null = null;
    const MOVE_TOOL_MAX_LIFT_CELLS = 5000;
    // Grid size of the layer currently viewed (tracks the sandbox so
    // entering/leaving a dimension reshapes the texture + coords), and
    // the last reported view depth for the breadcrumb.
    let viewW = props.width;
    let viewH = props.height;
    let lastDepth = 0;
    const palette: MaterialDef[] = [];

    (async () => {
      try {
        const bundle = await loadSandbox({
          width: props.width,
          height: props.height,
          seed: 0xc0ffee,
        });
        if (cancelled) return;
        bundleRef.current = bundle;
        palette.push(...bundle.materials);
        const renderer = await createRenderer(canvas, props.width, props.height, palette);
        if (cancelled) {
          renderer.dispose();
          return;
        }
        rendererRef.current = renderer;
        props.onReady(bundle);

        const tick = () => {
          if (cancelled) return;
          const b = bundleRef.current;
          const r = rendererRef.current;
          if (!b || !r) return;
          // Follow the viewed layer's grid size: entering a dimension
          // swaps the main 256x256 world for a smaller child, so the
          // data texture and the click→cell mapping track
          // sandbox.width()/height() rather than the fixed props. Only
          // the texture resolution changes; the canvas keeps its
          // on-screen size, so a 128x128 dimension fills the same
          // square (upscaled), which is what we want for editing it.
          const sw = b.sandbox.width();
          const sh = b.sandbox.height();
          if (sw !== viewW || sh !== viewH) {
            viewW = sw;
            viewH = sh;
            r.setGrid(sw, sh);
          }
          const depth = b.sandbox.layerDepth?.() ?? 0;
          if (depth !== lastDepth) {
            lastDepth = depth;
            propsRef.current.onLayerChange(depth);
          }
          // Per-frame brush applies only while a non-shape paint
          // tool is active. Action mode AND code mode are one-shots
          // at pointerdown (handled in `onDown` below): action mode
          // so dragging across a switch doesn't oscillate it 60
          // times per second, and code mode so clicking a CPU to
          // open the Code Tool doesn't simultaneously paint (or, if
          // the selected material is `empty`, erase) the cell under
          // the cursor.
          const brushing =
            pointerDown
            && propsRef.current.selectedShape === null
            && !propsRef.current.actionMode
            && !propsRef.current.codeMode
            && !propsRef.current.dimensionMode
            && !propsRef.current.zoomToolActive
            && !propsRef.current.moveMode;
          if (brushing) {
            applyBrush(b, lastX, lastY);
          }
          // Skip physics ONLY while the user is brushing a *solid*
          // material — metals, alloys, etc. — so the rigid shape
          // they're sketching doesn't drop out from under them
          // mid-stroke. Liquids, gases, and powders all keep
          // ticking during a brush so water flows, smoke rises,
          // and sand piles realistically as the user pours them
          // out. (The user explicitly asked for this split — see
          // the "metals keep their drawn shape; everything else
          // reacts to gravity as drawn" feedback.)
          const selectedMat = b.materials[propsRef.current.selectedMaterialId];
          const isSolidBrush =
            brushing && selectedMat?.behavior === "solid";
          if (!propsRef.current.paused && !isSolidBrush) {
            b.sandbox.tick(0.016);
          }
          r.update(b.sandbox.snapshotMaterials(), b.sandbox.snapshotSeeds());
          r.render();
          updatePreview();
          updateMagnifier();
          updateActionHover();
          propsRef.current.onTickInfo({
            tick: b.sandbox.tickCount(),
            backend: `${b.backend} / ${r.backend()}`,
          });
          rafId = requestAnimationFrame(tick);
        };
        rafId = requestAnimationFrame(tick);
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        setError(msg);
      }
    })();

    const toGrid = (clientX: number, clientY: number): [number, number] => {
      const rect = canvas.getBoundingClientRect();
      // Map against the *viewed* layer's size, not the props, so clicks
      // land on the right cell when inside a smaller dimension.
      const x = ((clientX - rect.left) / rect.width) * viewW;
      const y = ((clientY - rect.top) / rect.height) * viewH;
      return [Math.floor(x), Math.floor(y)];
    };

    const applyBrush = (bundle: SandboxBundle, x: number, y: number) => {
      const p = propsRef.current;
      // Slider exposes brushRadius in 0.01 increments for finer-feel
      // control, but the wasm `paint_circle` / `heat_circle` take
      // integer cell radii. Round here so 1.50 paints visibly larger
      // than 1.49 instead of both truncating to 1.
      const r = Math.max(0, Math.round(p.brushRadius));
      if (p.heatMode) {
        bundle.sandbox.heatCircle(x, y, r, p.brushKelvin);
      } else {
        bundle.sandbox.paintCircle(x, y, r, p.selectedMaterialId);
      }
    };

    const SHAPE_DRAG_PIXELS_PER_STEP = 8;

    const updateActionHover = () => {
      const overlay = actionHoverRef.current;
      if (!overlay) return;
      const p = propsRef.current;
      const b = bundleRef.current;
      // Reasons to hide the ring: neither action nor code mode is
      // on, cursor not on the canvas, or sandbox not yet ready.
      // Each gets the same `display: none` short-circuit.
      if ((!p.actionMode && !p.codeMode) || !cursorOnCanvas || !b) {
        if (overlay.style.display !== "none") {
          overlay.style.display = "none";
        }
        return;
      }
      const w = p.width;
      const h = p.height;
      if (lastX < 0 || lastY < 0 || lastX >= w || lastY >= h) {
        overlay.style.display = "none";
        return;
      }
      // Ask the engine if the cursor is over an actionable cell:
      // a trigger material in action mode, or a CPU machine in code
      // mode. Cheap (a cell read + a lookup); polling per frame is
      // fine. Hides whenever the cell isn't actionable for the mode.
      const actionable = p.actionMode
        ? b.sandbox.isTriggerAt(lastX, lastY)
        : (b.sandbox.isCodeAt?.(lastX, lastY) ?? false);
      if (!actionable) {
        overlay.style.display = "none";
        return;
      }
      // Big circular ring centred on the cursor cell. The radius
      // is in cells so the ring scales with the canvas zoom; at
      // pixelScale 2 that's a ~14 px-wide CSS circle, big
      // enough to be obvious on touch + on a busy scene without
      // hiding the cell underneath.
      const px = p.pixelScale;
      const ringCells = 7;
      const sizePx = ringCells * px;
      const ox = canvas.offsetLeft;
      const oy = canvas.offsetTop;
      const cellPx = px;
      // Centre the ring on the middle of the hovered cell.
      const left = ox + lastX * cellPx + cellPx / 2 - sizePx / 2;
      const top = oy + lastY * cellPx + cellPx / 2 - sizePx / 2;
      overlay.style.display = "block";
      overlay.style.left = `${left}px`;
      overlay.style.top = `${top}px`;
      overlay.style.width = `${sizePx}px`;
      overlay.style.height = `${sizePx}px`;
    };

    const updatePreview = () => {
      const overlay = previewRef.current;
      const label = previewLabelRef.current;
      if (!overlay) return;
      const shape = propsRef.current.selectedShape;
      if (!pointerDown || !shape) {
        if (overlay.style.display !== "none") {
          overlay.style.display = "none";
        }
        return;
      }
      const px = propsRef.current.pixelScale;
      const bounds = shapeBounds(shape, shapeScale);
      const ox = canvas.offsetLeft;
      const oy = canvas.offsetTop;
      const left = ox + (shapeAnchorX + bounds.minX) * px;
      const top = oy + (shapeAnchorY + bounds.minY) * px;
      const width = (bounds.maxX - bounds.minX + 1) * px;
      const height = (bounds.maxY - bounds.minY + 1) * px;
      overlay.style.display = "block";
      overlay.style.left = `${left}px`;
      overlay.style.top = `${top}px`;
      overlay.style.width = `${width}px`;
      overlay.style.height = `${height}px`;
      if (label) {
        label.textContent = `${shape.name} ×${shapeScale}`;
      }
    };

    // Magnifier: copy a slice of the main WebGL canvas into the
    // overlay's 2D canvas at higher zoom. Source rect is sized
    // (DIAMETER / zoom) — sampling fewer source pixels and stretching
    // them up at the destination. `imageSmoothingEnabled = false`
    // keeps the pixel art crisp when sandbox cells are visible at
    // 1:1 to the simulation grid (e.g. zoom > pixelScale).
    const updateMagnifier = () => {
      const wrapper = magnifierRef.current;
      const dst = magnifierCanvasRef.current;
      if (!wrapper || !dst) return;
      const p = propsRef.current;
      if (!p.magnifierActive || !cursorOnCanvas) {
        if (wrapper.style.display !== "none") wrapper.style.display = "none";
        return;
      }
      // Position centred on the cursor, in canvas-host CSS pixels.
      wrapper.style.display = "block";
      wrapper.style.left = `${cursorHostX - MAGNIFIER_DIAMETER / 2}px`;
      wrapper.style.top = `${cursorHostY - MAGNIFIER_DIAMETER / 2}px`;
      const ctx = dst.getContext("2d");
      if (!ctx) return;
      const zoom = p.magnifierZoom;
      // Source diameter in canvas-backbuffer pixel units. drawImage's
      // source rect samples the backbuffer regardless of CSS sizing,
      // so we have to scale CSS values by the canvas's CSS-to-buffer
      // ratio. At magnifierZoom=k the magnifier shows 1/k of its own
      // diameter's worth of source pixels stretched up to its full
      // diameter at the destination.
      const cssToBuffer = canvas.width / canvas.clientWidth;
      const srcDiameter = (MAGNIFIER_DIAMETER * cssToBuffer) / zoom;
      // Cursor position translated from canvas-host CSS pixels into
      // canvas-backbuffer pixels.
      const bufCursorX = (cursorHostX - canvas.offsetLeft) * cssToBuffer;
      const bufCursorY = (cursorHostY - canvas.offsetTop) * cssToBuffer;
      const sx = bufCursorX - srcDiameter / 2;
      const sy = bufCursorY - srcDiameter / 2;
      ctx.imageSmoothingEnabled = false;
      // Clear first in case the cursor approaches the canvas edge and
      // drawImage samples out of bounds (transparent pixels would
      // otherwise ghost the previous frame's content).
      ctx.fillStyle = "#000";
      ctx.fillRect(0, 0, MAGNIFIER_DIAMETER, MAGNIFIER_DIAMETER);
      ctx.drawImage(
        canvas,
        sx,
        sy,
        srcDiameter,
        srcDiameter,
        0,
        0,
        MAGNIFIER_DIAMETER,
        MAGNIFIER_DIAMETER,
      );
    };

    const onDown = (e: PointerEvent) => {
      pointerDown = true;
      [lastX, lastY] = toGrid(e.clientX, e.clientY);
      canvas.setPointerCapture(e.pointerId);
      shapeAnchorX = lastX;
      shapeAnchorY = lastY;
      shapeScale = 1;
      dragHasMoved = false;
      // Move tool: one-shot per pointerdown. If we're holding a
      // lifted region from a previous double-click, drop it at the
      // click point (re-painting each cell at click + offset);
      // otherwise start panning the canvas-host scroll. Skip every
      // other click-driven mode for this event.
      if (propsRef.current.moveMode) {
        const b = bundleRef.current;
        const host = hostRef.current;
        if (b && liftedCells) {
          for (const c of liftedCells) {
            const x = lastX + c.dx;
            const y = lastY + c.dy;
            if (x < 0 || y < 0 || x >= b.sandbox.width() || y >= b.sandbox.height()) {
              continue;
            }
            b.sandbox.paintCircle(x, y, 0, c.material);
          }
          liftedCells = null;
        } else if (host) {
          movePanning = true;
          panStartClientX = e.clientX;
          panStartClientY = e.clientY;
          panStartScrollLeft = host.scrollLeft;
          panStartScrollTop = host.scrollTop;
        }
        return;
      }
      // Zoom tool: one-shot per click. Step the canvas zoom one rung
      // (in by default, out with Shift) and stash the anchor so the
      // resize effect can re-scroll the host to keep the clicked
      // cell under the cursor. Skip every other click-driven mode
      // for this event.
      if (propsRef.current.zoomToolActive) {
        const host = hostRef.current;
        if (host) {
          const rect = host.getBoundingClientRect();
          const direction: 1 | -1 = e.shiftKey ? -1 : 1;
          const current = propsRef.current.pixelScale;
          const next = propsRef.current.computeNextZoom(current, direction);
          if (next !== current) {
            pendingZoomAnchorRef.current = {
              gridX: lastX + 0.5,
              gridY: lastY + 0.5,
              hostX: e.clientX - rect.left,
              hostY: e.clientY - rect.top,
              targetScale: next,
            };
            propsRef.current.onPixelScaleChange(next);
          }
        }
        return;
      }
      // Action mode is one-shot per click — fire the toggle here
      // and let the per-frame brush stay disabled. The user
      // dragging the cursor across a powered scene shouldn't
      // oscillate every switch they pass over.
      const b = bundleRef.current;
      if (b && propsRef.current.actionMode) {
        b.sandbox.toggleAt(lastX, lastY);
      }
      // Code mode is also one-shot per click: if the clicked cell
      // belongs to a CPU machine, open the Code Tool panel for it.
      if (b && propsRef.current.codeMode && b.sandbox.isCodeAt?.(lastX, lastY)) {
        propsRef.current.onCodeClick(lastX, lastY);
      }
      // Dimension mode: clicking a portal cell enters its child world.
      // The render loop picks up the size change and reshapes the
      // view; the depth report drives App's breadcrumb.
      if (
        b
        && propsRef.current.dimensionMode
        && b.sandbox.isDimensionAt?.(lastX, lastY)
      ) {
        b.sandbox.enterDimension?.(lastX, lastY);
      }
    };
    const onMove = (e: PointerEvent) => {
      [lastX, lastY] = toGrid(e.clientX, e.clientY);
      cursorOnCanvas = true;
      const rect = canvas.parentElement?.getBoundingClientRect();
      if (rect) {
        cursorHostX = e.clientX - rect.left;
        cursorHostY = e.clientY - rect.top;
      }
      // Move-tool panning: while held, scroll the canvas-host by
      // the cursor delta so the world drags with the pointer.
      // Negate the delta: dragging right scrolls the content right
      // = scrollLeft decreases.
      if (movePanning) {
        const host = hostRef.current;
        if (host) {
          host.scrollLeft = panStartScrollLeft - (e.clientX - panStartClientX);
          host.scrollTop = panStartScrollTop - (e.clientY - panStartClientY);
        }
        return;
      }
      if (pointerDown && propsRef.current.selectedShape !== null) {
        const dx = lastX - shapeAnchorX;
        const dy = lastY - shapeAnchorY;
        const dist = Math.max(Math.abs(dx), Math.abs(dy));
        const nextScale = 1 + Math.floor(dist / SHAPE_DRAG_PIXELS_PER_STEP);
        if (nextScale !== shapeScale) {
          shapeScale = nextScale;
          dragHasMoved = true;
        }
      }
    };
    const onUp = (e: PointerEvent) => {
      const wasDown = pointerDown;
      pointerDown = false;
      canvas.releasePointerCapture(e.pointerId);
      // Move-tool: end any in-progress pan. The drop-from-lifted
      // path completed on pointerdown, so nothing to do here.
      if (movePanning) {
        movePanning = false;
        return;
      }
      const shape = propsRef.current.selectedShape;
      const b = bundleRef.current;
      if (wasDown && shape && b) {
        const finalScale = dragHasMoved ? shapeScale : 1;
        stampShape(b.sandbox, shape, shapeAnchorX, shapeAnchorY, finalScale);
      }
    };
    const onLeave = () => {
      cursorOnCanvas = false;
      // Treat pointerleave like pointerup for the brush — drawing
      // off-canvas would otherwise keep firing on the last-known
      // cell while the cursor is somewhere else.
      pointerDown = false;
    };

    // Double-click toggles a `tags: [trigger]` cell at the click
    // location (today: on/off switches). The dblclick browser
    // event fires after the second click's pointerup, so the
    // single-click paint flow has already completed and the
    // engine knows whatever was painted on the first click. We
    // grab the cell at the dblclick coords and call toggleAt;
    // the engine no-ops if the cell isn't a trigger material, so
    // a stray double-click on regular sand or water does nothing.
    const onDouble = (e: MouseEvent) => {
      const b = bundleRef.current;
      if (!b) return;
      const [x, y] = toGrid(e.clientX, e.clientY);
      // Move-tool double-click: lift the 4-connected same-material
      // region starting at (x, y) into the `liftedCells` buffer.
      // The next single click drops it back at the new location.
      // BFS bounded by MOVE_TOOL_MAX_LIFT_CELLS so an accidental
      // double-click on a giant ocean of water doesn't lift the
      // whole map. State bytes (gate orientations etc.) aren't
      // currently exposed by the sandbox snapshot API, so the lift
      // captures material only — fine for water, sand, walls;
      // wires + gates re-land with default orientation.
      if (propsRef.current.moveMode) {
        const w = b.sandbox.width();
        const h = b.sandbox.height();
        if (x < 0 || y < 0 || x >= w || y >= h) return;
        const mats = b.sandbox.snapshotMaterials();
        const baseMat = mats[y * w + x];
        if (baseMat === 0) return;
        const visited = new Uint8Array(w * h);
        visited[y * w + x] = 1;
        const lifted: { dx: number; dy: number; material: number }[] = [];
        let head = 0;
        const queue: number[] = [y * w + x];
        while (head < queue.length && lifted.length < MOVE_TOOL_MAX_LIFT_CELLS) {
          const idx = queue[head++];
          const cx = idx % w;
          const cy = (idx - cx) / w;
          lifted.push({ dx: cx - x, dy: cy - y, material: baseMat });
          for (const [ddx, ddy] of [
            [1, 0],
            [-1, 0],
            [0, 1],
            [0, -1],
          ] as const) {
            const nx = cx + ddx;
            const ny = cy + ddy;
            if (nx < 0 || ny < 0 || nx >= w || ny >= h) continue;
            const nidx = ny * w + nx;
            if (visited[nidx]) continue;
            if (mats[nidx] !== baseMat) continue;
            visited[nidx] = 1;
            queue.push(nidx);
          }
        }
        for (const c of lifted) {
          b.sandbox.paintCircle(x + c.dx, y + c.dy, 0, 0);
        }
        liftedCells = lifted;
        return;
      }
      b.sandbox.toggleAt(x, y);
    };

    canvas.addEventListener("pointerdown", onDown);
    canvas.addEventListener("pointermove", onMove);
    canvas.addEventListener("pointerup", onUp);
    canvas.addEventListener("pointerleave", onLeave);
    canvas.addEventListener("dblclick", onDouble);

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
      canvas.removeEventListener("pointerdown", onDown);
      canvas.removeEventListener("pointermove", onMove);
      canvas.removeEventListener("pointerup", onUp);
      canvas.removeEventListener("pointerleave", onLeave);
      canvas.removeEventListener("dblclick", onDouble);
      rendererRef.current?.dispose();
      rendererRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.width, props.height]);

  // Resize effect — reacts to pixelScale changes without reinitialising
  // the sandbox or the renderer. Updates the canvas backbuffer +
  // CSS size and tells three.js the new dimensions; the active rAF
  // loop just renders into the new size on its next tick.
  //
  // After resizing, if the zoom-tool stashed a click anchor, we
  // scroll the canvas-host so the same grid cell lands at the same
  // CSS offset within the host — i.e., the clicked cell stays under
  // the cursor across the zoom step. The anchor only fires when the
  // pixelScale has caught up to what the click requested, so a
  // simultaneous toolbar zoom doesn't get re-anchored to a stale
  // click.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const w = props.width * props.pixelScale;
    const h = props.height * props.pixelScale;
    canvas.width = w;
    canvas.height = h;
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;
    rendererRef.current?.resize(canvas.clientWidth, canvas.clientHeight);

    const anchor = pendingZoomAnchorRef.current;
    const host = hostRef.current;
    if (anchor && host && anchor.targetScale === props.pixelScale) {
      // After the resize the clicked grid cell sits at
      // (gridX * pixelScale, gridY * pixelScale) in canvas CSS
      // coords. We want it to land at (hostX, hostY) inside the
      // host viewport — so scroll = cellCss - hostCoord. clamp via
      // scrollTo's natural clamping.
      host.scrollTo({
        left: anchor.gridX * props.pixelScale - anchor.hostX,
        top: anchor.gridY * props.pixelScale - anchor.hostY,
      });
      pendingZoomAnchorRef.current = null;
    }
  }, [props.width, props.height, props.pixelScale]);

  if (error) {
    return (
      <div className="canvas-host">
        <div style={{ color: "#ff8a8a" }}>Sim init failed: {error}</div>
      </div>
    );
  }

  const hostClass = "canvas-host" + (props.backgroundClass ? ` ${props.backgroundClass}` : "");
  return (
    <div ref={hostRef} className={hostClass}>
      <canvas ref={canvasRef} className="sim" />
      <div ref={previewRef} className="shape-preview" style={{ display: "none" }}>
        <span ref={previewLabelRef} className="shape-preview-label" />
      </div>
      <div
        ref={actionHoverRef}
        className="action-hover"
        style={{ display: "none" }}
        aria-hidden="true"
      />
      <div
        ref={magnifierRef}
        className="magnifier"
        style={{
          display: "none",
          width: `${MAGNIFIER_DIAMETER}px`,
          height: `${MAGNIFIER_DIAMETER}px`,
        }}
      >
        <canvas
          ref={magnifierCanvasRef}
          width={MAGNIFIER_DIAMETER}
          height={MAGNIFIER_DIAMETER}
        />
      </div>
    </div>
  );
}
